import { useEffect, useState, useRef } from "react";
import { toast } from "@/lib/toast";
import { RefreshCw } from "lucide-react";
import { Button } from "./ui/button";

export const AppUpdateManager = () => {
    const [updateAvailable, setUpdateAvailable] = useState(false);
    const [updateFn, setUpdateFn] = useState<(() => Promise<void>) | null>(null);
    const inactivityTimer = useRef<NodeJS.Timeout | null>(null);
    const lastActivity = useRef<number>(Date.now());
    const INACTIVITY_LIMIT = 60 * 1000; // 1 minute in milliseconds

    const handleUpdate = async () => {
        if (updateFn) {
            toast.info("Bezig met updaten naar de nieuwste versie...", { channel: 'general' });
            await updateFn();
        }
    };

    const resetInactivityTimer = () => {
        lastActivity.current = Date.now();
        if (inactivityTimer.current) {
            clearTimeout(inactivityTimer.current);
            inactivityTimer.current = null;
        }

        if (updateAvailable) {
            startInactivityTimer();
        }
    };

    const startInactivityTimer = () => {
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);

        inactivityTimer.current = setTimeout(() => {
            const now = Date.now();
            if (now - lastActivity.current >= INACTIVITY_LIMIT) {
                console.log("[AppUpdateManager] User inactive for > 1min, forcing update.");
                handleUpdate();
            }
        }, INACTIVITY_LIMIT);
    };

    useEffect(() => {
        const onUpdateAvailable = (event: any) => {
            console.log("[AppUpdateManager] Update notification received");
            setUpdateAvailable(true);
            setUpdateFn(() => event.detail.updateServiceWorker);

            toast.info("Nieuwe versie beschikbaar", {
                channel: 'general',
                description: "Er is een nieuwe versie van de app beschikbaar. Vernieuw de pagina om deze te laden.",
                duration: Infinity,
                action: {
                    label: "Nu vernieuwen",
                    onClick: () => {
                        event.detail.updateServiceWorker();
                    }
                }
            });

            startInactivityTimer();
        };

        window.addEventListener('app-update-available', onUpdateAvailable);

        const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
        activityEvents.forEach(event => {
            window.addEventListener(event, resetInactivityTimer);
        });

        return () => {
            window.removeEventListener('app-update-available', onUpdateAvailable);
            activityEvents.forEach(event => {
                window.removeEventListener(event, resetInactivityTimer);
            });
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        };
    }, [updateAvailable]);

    return null; // This component doesn't render anything itself, it uses toasts
};
